import { addVariantToCart, setQuantity } from '@theme/mrb-home';

const FEEDBACK_MS = 2000;

/**
 * @typedef {{ id: number, available: boolean, options: string[], price: string, compare: string,
 *   discount: number, cantidad: string, mediaId: number | null }} MrbVariant
 */

/**
 * Mr. Brown product page: gallery, variant picker, quantity, add to cart, share and the mobile sticky bar.
 */
class MrbProduct extends HTMLElement {
  /** @type {MrbVariant[]} */
  #variants = [];
  /** @type {IntersectionObserver | null} */
  #stickyObserver = null;

  connectedCallback() {
    const json = this.querySelector('script[data-pdp-variants]')?.textContent;
    try {
      this.#variants = json ? JSON.parse(json) : [];
    } catch (error) {
      console.error(error);
    }

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
    this.form?.addEventListener('submit', this.#handleSubmit);
    this.track?.addEventListener('scroll', this.#syncThumbs, { passive: true });
    this.sticky?.addEventListener('click', this.#handleClick);
    this.#observeSticky();
    this.#syncQuantity();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    this.form?.removeEventListener('submit', this.#handleSubmit);
    this.track?.removeEventListener('scroll', this.#syncThumbs);
    this.sticky?.removeEventListener('click', this.#handleClick);
    this.#stickyObserver?.disconnect();
  }

  /** @returns {HTMLFormElement | null} */
  get form() {
    return this.querySelector('form.mrb-pdp__form');
  }

  /** @returns {HTMLElement | null} */
  get track() {
    return this.querySelector('[data-pdp-track]');
  }

  /** @returns {HTMLElement | null} */
  get sticky() {
    return this.closest('section')?.querySelector('[data-pdp-sticky]') ?? null;
  }

  /** @returns {HTMLInputElement | null} */
  get quantityInput() {
    const input = this.querySelector('[data-pdp-qty]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @returns {HTMLElement[]} */
  get #scopes() {
    const sticky = this.sticky;
    return sticky ? [this, sticky] : [this];
  }

  /**
   * @param {string} selector
   * @returns {HTMLElement[]}
   */
  #all(selector) {
    return this.#scopes.flatMap((scope) => [...scope.querySelectorAll(selector)]).filter((el) => el instanceof HTMLElement);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const thumb = target.closest('[data-pdp-thumb]');
    if (thumb instanceof HTMLElement) {
      this.showSlide(Number(thumb.dataset.pdpThumb));
      return;
    }

    const step = target.closest('[data-pdp-qty-step]');
    if (step instanceof HTMLButtonElement && this.quantityInput) {
      setQuantity(this.quantityInput, Number(this.quantityInput.value) + Number(step.dataset.pdpQtyStep));
      this.#syncQuantity();
      return;
    }

    const share = target.closest('[data-pdp-share]');
    if (share instanceof HTMLButtonElement) {
      this.#share(share);
      return;
    }

    const wishlist = target.closest('[data-pdp-wishlist]');
    if (wishlist instanceof HTMLButtonElement) {
      wishlist.setAttribute('aria-pressed', String(wishlist.getAttribute('aria-pressed') !== 'true'));
    }
  };

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-pdp-qty]')) {
      setQuantity(target, Number(target.value));
      this.#syncQuantity();
    } else if (target.matches('[data-pdp-option]')) {
      this.#selectVariantFromOptions();
    }
  };

  #syncQuantity() {
    const value = this.quantityInput?.value ?? '1';
    for (const mirror of this.#all('[data-pdp-qty-mirror]')) mirror.textContent = value;
    const quantity = Number(value);
    for (const step of this.#all('[data-pdp-qty-step="-1"]')) {
      if (step instanceof HTMLButtonElement && !this.quantityInput?.disabled) step.disabled = quantity <= 1;
    }
  }

  /** @param {number} index */
  showSlide(index) {
    const track = this.track;
    const slide = track?.querySelectorAll('[data-pdp-slide]')[index];
    if (!track || !(slide instanceof HTMLElement)) return;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    this.#setActiveThumb(index);
  }

  /** @param {number} index */
  #setActiveThumb(index) {
    this.querySelectorAll('[data-pdp-thumb]').forEach((thumb, current) =>
      thumb.setAttribute('aria-selected', String(current === index))
    );
  }

  #syncThumbs = () => {
    const track = this.track;
    if (!track || track.clientWidth === 0) return;
    this.#setActiveThumb(Math.round(track.scrollLeft / track.clientWidth));
  };

  #selectVariantFromOptions() {
    /** @type {string[]} */
    const selected = [];
    for (const input of this.querySelectorAll('[data-pdp-option]:checked')) {
      if (input instanceof HTMLInputElement) selected[Number(input.dataset.pdpOption)] = input.value;
    }

    const variant = this.#variants.find((candidate) =>
      candidate.options.every((value, index) => selected[index] === undefined || selected[index] === value)
    );
    this.#renderVariant(variant);
  }

  /** @param {MrbVariant | undefined} variant */
  #renderVariant(variant) {
    const idInput = this.querySelector('[data-pdp-variant-id]');
    if (idInput instanceof HTMLInputElement && variant) idInput.value = String(variant.id);

    const submit = this.querySelector('[data-pdp-submit]');
    const texts = submit instanceof HTMLElement ? submit.dataset : {};
    const available = Boolean(variant?.available);
    const label = !variant ? texts.textUnavailable : available ? texts.textBuy : texts.textSoldOut;

    for (const button of this.#all('[data-pdp-submit]')) {
      if (button instanceof HTMLButtonElement) button.disabled = !available;
      const text = button.querySelector('[data-pdp-submit-label]');
      if (text && label) text.textContent = label;
    }
    for (const control of this.#all('[data-pdp-qty], [data-pdp-qty-step]')) {
      if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) control.disabled = !available;
    }
    this.#syncQuantity();

    const soldOut = this.querySelector('[data-pdp-sold-out]');
    if (soldOut instanceof HTMLElement) soldOut.hidden = available || !variant;

    if (!variant) return;

    for (const price of this.#all('[data-pdp-price]')) price.textContent = variant.price;
    const priceBox = this.querySelector('[data-pdp-price-box]');
    if (priceBox instanceof HTMLElement) priceBox.dataset.discounted = String(Boolean(variant.compare));
    const compare = this.querySelector('[data-pdp-compare]');
    if (compare) compare.textContent = variant.compare;
    const priceLabel = this.querySelector('[data-pdp-price-label]');
    if (priceLabel instanceof HTMLElement) {
      priceLabel.textContent = (variant.compare ? priceLabel.dataset.labelSale : priceLabel.dataset.labelRegular) ?? '';
    }
    const badge = this.querySelector('[data-pdp-badge-value]');
    if (badge) badge.textContent = `${variant.discount}%`;
    const savings = this.querySelector('[data-pdp-savings]');
    if (savings) savings.textContent = String(variant.discount);
    const cantidad = this.querySelector('[data-pdp-cantidad]');
    if (cantidad && variant.cantidad) cantidad.textContent = variant.cantidad;

    if (variant.mediaId) {
      const slides = [...this.querySelectorAll('[data-pdp-slide]')];
      const index = slides.findIndex((slide) => slide instanceof HTMLElement && slide.dataset.mediaId === String(variant.mediaId));
      if (index >= 0) this.showSlide(index);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState(window.history.state, '', url);
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    event.preventDefault();
    const form = this.form;
    const variantId = form?.querySelector('[data-pdp-variant-id]');
    if (!(variantId instanceof HTMLInputElement) || !variantId.value) return;

    const buttons = this.#all('[data-pdp-submit]').filter((button) => button instanceof HTMLButtonElement);
    if (buttons.some((button) => button.getAttribute('aria-busy') === 'true')) return;

    const main = this.querySelector('[data-pdp-submit]');
    const texts = main instanceof HTMLElement ? main.dataset : {};
    const labels = buttons.map((button) => button.querySelector('[data-pdp-submit-label]'));
    const original = labels.map((label) => label?.textContent ?? '');
    const setLabels = (/** @type {string | undefined} */ text) =>
      labels.forEach((label, index) => {
        if (label) label.textContent = text ?? original[index] ?? '';
      });

    buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));
    setLabels(texts.textAdding);

    try {
      await addVariantToCart({
        variantId: variantId.value,
        quantity: Number(this.quantityInput?.value) || 1,
        productId: this.dataset.productId,
        source: 'mrb-main-product',
        openDrawer: this.dataset.openDrawer !== 'false',
      });
      setLabels(texts.textAdded);
    } catch (error) {
      console.error(error);
      setLabels(texts.textError);
    } finally {
      window.setTimeout(() => {
        setLabels(undefined);
        buttons.forEach((button) => button.removeAttribute('aria-busy'));
      }, FEEDBACK_MS);
    }
  };

  /** @param {HTMLButtonElement} button */
  async #share(button) {
    const { shareTitle: title, shareText: text, shareUrl: url = window.location.href } = button.dataset;

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt(button.dataset.textCopied ?? '', url);
      return;
    }

    const label = button.querySelector('[data-pdp-share-label]');
    const status = this.querySelector('[data-pdp-status]');
    const copied = button.dataset.textCopied ?? '';
    const original = label?.textContent ?? '';
    if (label) label.textContent = copied;
    if (status) status.textContent = copied;
    window.setTimeout(() => {
      if (label) label.textContent = original;
      if (status) status.textContent = '';
    }, FEEDBACK_MS);
  }

  #observeSticky() {
    const sticky = this.sticky;
    const buy = this.querySelector('[data-pdp-buy]');
    if (!sticky || !buy || !('IntersectionObserver' in window)) return;

    this.#stickyObserver = new IntersectionObserver(([entry]) => {
      const visible = !entry?.isIntersecting && (entry?.boundingClientRect.top ?? 0) < 0;
      sticky.dataset.visible = String(visible);
      sticky.toggleAttribute('inert', !visible);
      sticky.setAttribute('aria-hidden', String(!visible));
    });
    this.#stickyObserver.observe(buy);
  }
}

/**
 * "Productos similares": swaps the server-rendered collection fallback for Shopify product recommendations
 * when the recommendations endpoint returns any.
 */
class MrbProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.url) this.#load(this.dataset.url);
  }

  /** @param {string} url */
  async #load(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Recommendations failed: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = html.querySelector('mrb-product-recommendations');
      const nextTrack = incoming?.querySelector('[data-similar-track]');
      const track = this.querySelector('[data-similar-track]');
      if (incoming instanceof HTMLElement && incoming.dataset.hasRecommendations === 'true' && nextTrack && track) {
        track.replaceChildren(...nextTrack.children);
        track.scrollLeft = 0;
        this.dataset.hasRecommendations = 'true';
      }
    } catch (error) {
      console.error(error);
    }

    this.hidden = !this.querySelector('[data-carousel-slide]');
    const carousel = /** @type {HTMLElement & { refresh?: () => void } | null} */ (this.querySelector('mrb-carousel'));
    carousel?.refresh?.();
  }
}

if (!customElements.get('mrb-product')) customElements.define('mrb-product', MrbProduct);
if (!customElements.get('mrb-product-recommendations')) {
  customElements.define('mrb-product-recommendations', MrbProductRecommendations);
}
